/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import isUndefined from 'lodash.isundefined';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {updateDeviceFiles} from '@ion/core/src/services/Connect';
import {getAWSFileUploadHeader} from '@ion/core/src/services/ConnectUtils';
import {DeviceFile} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import getGlobalProp from '@ion/dls/src/props/GlobalPropsConfig';
import {hasChangeInColumnState, setGridColumnState} from '../helpers';

/**
 * The resolveDeviceFile utility for uploading device files and release notes.
 *
 * * ```typescript
 *
 * import {resolveDeviceFile} from '@ion/applications/src/utils/helpers';
 *
 * resolveDeviceFile(file, deviceFileData, releaseNoteData);
 *
 * ```
 */
export const resolveDeviceFile = async(
  file: DeviceFile,
  deviceFile,
  releaseNotes
): Promise<unknown> => {
  return await updateDeviceFiles(resolveAWSDeviceFile(
    file,
    deviceFile?.[0]?.file,
    releaseNotes?.[0]?.file
  ));
};

/**
 * The resolveAWSDeviceFile utility for uploading device files and release notes.
 *
 * * ```typescript
 *
 * import {resolveAWSDeviceFile} from '@ion/applications/src/utils/helpers';
 *
 * const header = resolveAWSDeviceFile(file, deviceFileData, releaseFileData);
 *
 * ```
 */
export const resolveAWSDeviceFile = (file: DeviceFile, deviceFileData, releaseFileData) => {
  let deviceHeader = {};
  let releaseHeader = {};
  let deviceFileUrl;
  let releaseNoteUrl;
  if (file?.uploads) {
    const deviceFileField  = file.uploads.data?.signedUrl?.fields;
    deviceFileUrl          = file.uploads.data?.signedUrl?.url;
    const releaseNoteField = file.uploads.releaseNote?.signedUrl?.fields;
    releaseNoteUrl         = file.uploads.releaseNote?.signedUrl?.url;
    if (deviceFileField) {
      deviceHeader = getAWSFileUploadHeader(deviceFileField, deviceFileData);
    } if (releaseNoteField) {
      releaseHeader = getAWSFileUploadHeader(releaseNoteField, releaseFileData);
    }
  }
  return {
    deviceFileUrl,
    deviceHeader,
    releaseNoteUrl,
    releaseHeader
  };
};

export const gridDefaultPageSize = 20;

/**
 * Provides a function which returns the standardized include Sub Groups preferences path.
 */
export const getSubGroupsPath = (page: string): string => `${page}:grid.viewSubGroups`;

/**
 * Provides a function which returns the standardized include Only This Account preferences path.
 */
export const getOnlyThisAccountPath = (page: string): string => `${page}:grid.viewOnlyThisAccount`;

/**
 * Provides a function which returns the standardized include Only This Account preferences path.
 */
export const getInheritableGroupPath = (page: string): string => `${page}:grid.hideInheritedGroups`;

/**
 * Provides a function which returns the standardized exclude inherited group preferences path.
 */
export const getExcludeInheritedGroupPath = (page: string): string => `${page}:grid.excludeInheritedGroups`;

/**
 * It will return the user persisted view sub groups state for the given grid.
 *
 * ```typescript
 *
 *    import {getViewSubGroupsState} from '../helpers';
 *
 *    const PREFERENCE = 'devicetypes';
 *    ...
 *
 *    [INITIALIZE_DEVICE_TYPES] : (draft) => {
 *      draft.isInitialized = true;
 *      draft.grid.includeSubGroups = getViewSubGroupsState(PREFERENCE);
 *    }
 *
 * ```
 */
export const getViewSubGroupsState = (page: string): boolean => {
  const pageSubGroupView   = UserPreferences.get(getSubGroupsPath(page));
  const globalSubGroupView = UserPreferences.get(Preference.includeSubAccountsToggleSetup);
  return isUndefined(pageSubGroupView) ? globalSubGroupView : pageSubGroupView;
};

/**
 * The utility for save grid view sub groups state in user preferences based on path.
 *
 *  ```typescript
 *
 *    import {setViewSubGroupsState} from '../helpers';
 *
 *    const PREFERENCE = 'devicetypes';
 *
 *    setViewSubGroupsState(PREFERENCE, true);
 *
 * ```
 */
export const setViewSubGroupsState = (page: string, viewSubGroups: boolean): Promise<boolean> => {
  return UserPreferences.set(getSubGroupsPath(page), viewSubGroups, !UserPreferences.hasChangeDetection());
};

/**
 * It will return the user persisted view only this account state for the given grid.
 *
 * ```typescript
 *
 *    import {getViewOnlyThisAccountState} from '../helpers';
 *
 *    const PREFERENCE = 'devicetypes';
 *    ...
 *
 *    [INITIALIZE_DEVICE_TYPES] : (draft) => {
 *      draft.isInitialized = true;
 *      draft.grid.includeOnlyThisAccount = getViewOnlyThisAccountState(PREFERENCE);
 *    }
 *
 * ```
 */
export const getViewOnlyThisAccountState = (page: string): boolean => {
  return UserPreferences.get(getOnlyThisAccountPath(page)) ?? false;
};

/**
 * The utility for save grid view only this account state in user preferences based on path.
 *
 *  ```typescript
 *
 *    import {setViewOnlyThisAccountState} from '../helpers';
 *
 *    const PREFERENCE = 'devicetypes';
 *
 *    setViewOnlyThisAccountState(PREFERENCE, true);
 *
 * ```
 */
export const setViewOnlyThisAccountState = (page: string, viewOnlyThisAccount: boolean): Promise<boolean> => {
  return UserPreferences.set(getOnlyThisAccountPath(page), viewOnlyThisAccount, !UserPreferences.hasChangeDetection());
};

/**
 * It will return the user persisted view inherited group state for the given grid.
 *
 * ```typescript
 *
 *    import {getViewInheritableGroupState} from '../helpers';
 *
 *    const PREFERENCE = 'devicetypes';
 *    ...
 *
 *    [SET_INHERITABLE_GROUPS] : (draft) => {
 *      draft.grid.inheritableGroups = getViewInheritableGroupState(PREFERENCE);
 *    }
 *
 * ```
 */
export const getViewInheritableGroupState = (page: string): boolean => {
  return UserPreferences.get(getInheritableGroupPath(page)) ?? false;
};

/**
 * The utility View Inheritable groups in user preferences based on path.
 *
 *  ```typescript
 *
 *    import {setViewInheritableGroupState} from '../helpers';
 *
 *    const PREFERENCE = 'inheritableGroups';
 *
 *    setViewInheritableGroupState(PREFERENCE, true);
 *
 * ```
 */
export const setViewInheritableGroupState = (page: string, viewInheritableAccount: boolean): Promise<boolean> => {
  return UserPreferences.set(
    getInheritableGroupPath(page),
    viewInheritableAccount,
    !UserPreferences.hasChangeDetection()
  );
};

/**
 * It will return the user persisted view exclude inherited group state for the given grid.
 *
 * ```typescript
 *
 *    import {getViewExcludeInheritedGroupState} from '../helpers';
 *
 *    const PREFERENCE = 'groups';
 *    ...
 *
 *    [SET_INHERITABLE_GROUPS] : (draft) => {
 *      draft.grid.includeInheritedGroups = getViewExcludeInheritedGroupState(PREFERENCE);
 *    }
 *
 * ```
 */
export const getViewExcludeInheritedGroupState = (page: string): boolean => {
  return UserPreferences.get(getExcludeInheritedGroupPath(page)) ?? false;
};

/**
 * The utility Exclude Inherited groups in user preferences based on path.
 *
 *  ```typescript
 *
 *    import {setViewExcludeInheritedGroupState} from '../helpers';
 *
 *    const PREFERENCE = 'includeInheritedGroups';
 *
 *    setViewExcludeInheritedGroupState(PREFERENCE, true);
 *
 * ```
 */
export const setViewExcludeInheritedGroupState = (page: string, excludeInheritedGroups: boolean): Promise<boolean> => {
  return UserPreferences.set(
    getExcludeInheritedGroupPath(page),
    excludeInheritedGroups,
    !UserPreferences.hasChangeDetection()
  );
};

/**
 * Provides a function which returns the standardized Rows Per Pag preferences path.
 */
export const getGridPageSize = (page: string): string => `${page}:grid.pageSize`;

/**
 * It will return the user persisted rows per page state for the given grid.
 *
 * ```typescript
 *
 *    import {getGridPageSizeState} from '../helpers';
 *
 *    const PREFERENCE = 'devicetypes';
 *    ...
 *
 *    [INITIALIZE_DEVICE_TYPES] : (draft) => {
 *      draft.isInitialized = true;
 *      draft.grid.pageSize = getGridPageSizeState(PREFERENCE);
 *    }
 *
 * ```
 */
export const getGridPageSizeState = (page: string) => {
  return UserPreferences.get(getGridPageSize(page)) || 0;
};

/**
 * The utility for save grid rows per page state in user preferences based on path.
 *
 *  ```typescript
 *
 *    import {setGridPageSizeState} from '../helpers';
 *
 *    const PREFERENCE = 'devicetypes';
 *
 *    setGridPageSizeState(PREFERENCE, 50);
 *
 * ```
 */
export const setGridPageSizeState = (page: string, pageSize: number) => {
  return UserPreferences.set(getGridPageSize(page), pageSize, !UserPreferences.hasChangeDetection());
};

/**
 * Returns a preference key for persisting the "view sub-groups" state
 * when preference usage is enabled via global configuration.
 *
 * This utility centralizes the feature-flag check that determines
 * whether preference-based persistence should be applied.
 *
 * If preferences are disabled, `undefined` is returned so callers
 * can fall back to default (non-persisted) behavior.
 *
 * @param candidatePreferenceKey - The preference key to use when preferences are enabled.
 * @returns The preference key if enabled; otherwise `undefined`.
 *
 * ```typescript
 * const preferenceKey = getSubGroupPreferenceKey('grid.view.subGroups');
 *
 * if (preferenceKey) {
 *   savePreference(preferenceKey, true);
 * }
 * ```
 */
export const getSubGroupPreferenceKey = (
  candidatePreferenceKey: string
): string | undefined => {
  const isPreferenceEnabled = getGlobalProp(
    'grid.viewSubGroupsToggle.default.use.preference',
    true
  );

  return isPreferenceEnabled ? candidatePreferenceKey : undefined;
};

/**
 * The utility for set grid column state in redux and user preferences based on path
 * if the user enabled the Automatic Grid saving option.
 *
 * `grid.hasUpdate` is only recalculated when the grid is **not** loading.
 * While loading, its current value is preserved so that unsaved-changes state
 * set by a prior dispatch is not lost.
 *
 *  ```typescript
 *
 *    import {onSetGridColumns} from '@ion/applications/src/utils/helpers';
 *
 *    const PREFERENCE = 'admin-assets';
 *
 *    onSetGridColumns({
 *      grid,
 *      isLoading  : assets.loading,
 *      preference : PREFERENCE
 *    }, columnState);
 *
 * ```
 *
 * @param grid       grid redux state
 * @param isLoading  indicates the grid is currently loading data
 * @param preference page preference key
 * @param columnState column state which holds the previous and current state
 */
export const onSetGridColumns = ({grid, isLoading, preference}, columnState) => {
  const {
    preferedColumnState,
    nextColumnState,
    autoSaveGrid
  }                = columnState;
  const preCheck   = !isLoading && !autoSaveGrid;
  grid.hasUpdate   = !isLoading
    ? preCheck && hasChangeInColumnState(preferedColumnState, nextColumnState)
    : grid.hasUpdate;
  grid.columnState = nextColumnState;
  setGridColumnState(preference, nextColumnState, autoSaveGrid);
  autoSaveGrid && (grid.preferedColumnState = nextColumnState);
};
